/**
 * Derive a build's set-bonus block from its equipment and the item catalog.
 *
 * This used to be stored. A saved build carried, per active set, the full item
 * objects of every member in `active_items` and `inactive_items` — 5.3 KB a
 * build, and the largest thing left in `build_payload` after items became ids
 * (see docs/guide/egress-plan.md, item 15). Those lists were not display data:
 * they were the client's only source of *set membership*, because until the
 * catalog shipped the browser could see that two items shared a set id but not
 * which other items belonged to it. `catalog.setById.get(id).item_ids` answers
 * that directly now, so the block is computed rather than shipped.
 *
 * Extracted from `localSolve/hydrateBuild`, which is a port of the Python
 * `serialize_build` and is pinned against it by `hydrateParity.test.js`. Both
 * callers share this one implementation deliberately: a build assembled from a
 * local solve and a build opened from storage must produce the same block, or
 * they drift apart in exactly the way that port exists to prevent.
 */

import { CHARAC_IDS } from './localSolve/kernelRequest';

/**
 * Position of each characteristic in the kernel's dense order.
 *
 * `serialize_build` fills a vector indexed by CHARAC_IDS and then emits its
 * non-zero entries, so its effects come out in *that* order — not in the order
 * they happen to sit in the set's own bonus list.
 */
const CHARAC_ORDER = new Map(CHARAC_IDS.map((id, index) => [Number(id), index]));

/** Sets award nothing below two pieces, so the server never reports them. */
export const MIN_ACTIVE_PIECES = 2;

/** Every locale a localized field carries; mirrors `build_serializer.LOCALES`. */
export const LOCALES = ['fr', 'en', 'de', 'es', 'pt'];

/**
 * A set's localized name dict, in the shape `serialize_build` emits.
 *
 * Both sides used to collapse this to `name.fr || name.en`, which rendered the
 * French name in every language: `BuildSetsPanel` re-wrapped the bare string
 * under the active language key, so an English build showed "Panoplie de
 * Misère" while `lang` was correctly 'en'. Both carry the dict now, and
 * `hydrateParity.test.js` still holds them together.
 */
export function setNameField(nameField, setId) {
    if (nameField && typeof nameField === 'object'
        && LOCALES.some((locale) => nameField[locale])) {
        return nameField;
    }
    // A stored block from before the dict, or a set the catalog never named.
    // Which language a bare string is in is unknowable, so it goes under every
    // key rather than being labelled as one.
    const placeholder = (typeof nameField === 'string' && nameField)
        ? nameField
        : `Set ${setId}`;
    return Object.fromEntries(LOCALES.map((locale) => [locale, placeholder]));
}

/**
 * Tier bonuses in the shape `serialize_build` emits.
 *
 * Note the value is the effect's raw `max`, which is what `_set_bonus_cache`
 * reads — not `pick_effect_value`, and not the tier list `transform_set_bonuses`
 * produces for the set detail endpoint. Three shapes exist; this is the one the
 * solver path uses.
 */
export function tiersFor(itemSet, pieceCount) {
    const out = [];
    for (const bonus of itemSet?.bonuses || []) {
        const tier = Number(bonus?.num_items_required);
        if (!Number.isFinite(tier)) continue;
        const effects = [];
        for (const effect of bonus?.effects || []) {
            const characteristic = Number(effect?.characteristic);
            const value = Number(effect?.max) || 0;
            if (Number.isFinite(characteristic) && value !== 0) {
                effects.push({ characteristic, value: Math.trunc(value) });
            }
        }
        // Ceilings the tier puts on a characteristic rather than points it adds —
        // see backend `core/set_caps.py`. They stay out of `effects` because every
        // reader of that list sums `value`, and a summed ceiling is a bonus.
        const caps = capsFor(bonus);
        // Tiers whose every effect is zero are dropped server-side, because the
        // dense vector they build filters zeros and an all-zero tier is skipped.
        // A tier that only caps is still a tier: it changes the sheet.
        if (effects.length || caps.length) {
            effects.sort((a, b) => (
                (CHARAC_ORDER.get(a.characteristic) ?? Number.MAX_SAFE_INTEGER)
                - (CHARAC_ORDER.get(b.characteristic) ?? Number.MAX_SAFE_INTEGER)
            ));
            const entry = { tier, is_active: pieceCount >= tier, effects };
            if (caps.length) entry.caps = caps;
            out.push(entry);
        }
    }
    out.sort((a, b) => a.tier - b.tier);
    return out;
}

/** One tier's `{characteristic, value}` ceilings, in kernel characteristic order. */
function capsFor(bonus) {
    const caps = [];
    for (const cap of bonus?.caps || []) {
        const characteristic = Number(cap?.characteristic);
        const value = Number(cap?.value);
        if (Number.isFinite(characteristic) && Number.isFinite(value)) {
            caps.push({ characteristic, value: Math.trunc(value) });
        }
    }
    caps.sort((a, b) => (
        (CHARAC_ORDER.get(a.characteristic) ?? Number.MAX_SAFE_INTEGER)
        - (CHARAC_ORDER.get(b.characteristic) ?? Number.MAX_SAFE_INTEGER)
    ));
    return caps;
}

/**
 * The set-bonus block for a list of equipped items.
 *
 * @param {object} catalog  loaded item catalog (see services/itemCatalog)
 * @param {Array<object|null>} items  equipped items, resolved; holes are empty slots
 * @returns {object} keyed by set id as a string, in `serialize_build`'s shape
 */
export function deriveSetBonuses(catalog, items) {
    if (!catalog) return {};

    // Piece counts come from the equipped items' own set ids, exactly as
    // `_HydrateBuild.get_piece_count` does.
    const pieceCount = new Map();
    const equipped = new Set();
    for (const item of items || []) {
        if (!item) continue;
        if (item.id != null) equipped.add(Number(item.id));
        const setId = item.item_set_id;
        if (setId === null || setId === undefined) continue;
        pieceCount.set(setId, (pieceCount.get(setId) || 0) + 1);
    }

    const setBonuses = {};
    for (const [setId, count] of pieceCount) {
        if (count < MIN_ACTIVE_PIECES) continue;
        const itemSet = catalog.setById.get(setId);
        const memberIds = itemSet?.item_ids || [];

        const activeItems = [];
        const inactiveItems = [];
        for (const memberId of memberIds) {
            const member = catalog.itemById.get(memberId);
            if (!member) continue;
            (equipped.has(memberId) ? activeItems : inactiveItems).push(member);
        }

        setBonuses[String(setId)] = {
            name: setNameField(itemSet?.name, setId),
            pieces: count,
            max_pieces: memberIds.length,
            active_items: activeItems,
            inactive_items: inactiveItems,
            bonuses: tiersFor(itemSet, count),
        };
    }
    return setBonuses;
}
